import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import plantScanningAnimation from "../assets/Plant_Scanning.json";
import { analyzePlantImage, type PlantAnalysis } from "../lib/claude";
import { fileToBase64 } from "../lib/storage";
import { savePlant } from "../lib/plants";
import { fetchMyHouseholds } from "../lib/households";
import {
  AlertIcon,
  BackIcon,
  CameraIcon,
  DropletIcon,
  GalleryIcon,
  LeafIcon,
  SparkleIcon,
  SunIcon,
} from "../components/icons";

// KEEP IN SYNC visually with src/screens/AddPlantScreen.tsx in the RN project.

function healthPalette(status: string) {
  const s = (status || "").toLowerCase();
  if (s.includes("heal")) return { bg: "#dcf1e2", fg: "#1f5a39" };
  if (s.includes("mild")) return { bg: "#fef3c7", fg: "#92400e" };
  if (s.includes("seri") || s.includes("sick"))
    return { bg: "#fee2e2", fg: "#991b1b" };
  return { bg: "#e5e7eb", fg: "#374151" };
}

export default function AddPlantScreen() {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<PlantAnalysis | null>(null);
  const [nickname, setNickname] = useState("");
  const [saving, setSaving] = useState(false);
  const [manualNameMode, setManualNameMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [lastWatered, setLastWatered] = useState<string>("");

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setAnalysis(null);
    setManualNameMode(false);
    setManualName("");
    setLastWatered("");
    analyze(file);
  }

  async function analyze(file: File, manualNameHint?: string) {
    setAnalyzing(true);
    setManualNameMode(false);
    try {
      const base64 = await fileToBase64(file);
      const mimeType = file.type.includes("png") ? "image/png" : "image/jpeg";
      let location: string | undefined;
      try {
        const homes = await fetchMyHouseholds();
        location = homes[0]?.name || undefined;
      } catch {
        // Location context is optional — don't block analysis
      }
      const result = await analyzePlantImage(base64, mimeType, manualNameHint, location);
      setAnalysis(result);
      if (!result.identified && !manualNameHint) {
        setManualNameMode(true);
      }
    } catch (err) {
      console.error("[SPRIG] analyze error:", err);
      const msg = err instanceof Error ? err.message : String(err);
      const retry = confirm(
        `Something went wrong analyzing that photo.\n\n${msg}\n\nTry again?`
      );
      if (retry) {
        analyze(file, manualNameHint);
        return;
      }
      handleRetake();
    } finally {
      setAnalyzing(false);
    }
  }

  function handleSubmitManualName() {
    const name = manualName.trim();
    if (!name || !imageFile) return;
    analyze(imageFile, name);
  }

  async function handleSave() {
    if (!analysis) return;
    setSaving(true);
    try {
      const homes = await fetchMyHouseholds();
      if (homes.length === 0) {
        throw new Error("You must belong to a household to add plants.");
      }
      // Web takes the explicit householdId. For now we use the first one —
      // when we add a multi-household picker on Add Plant, this should come
      // from the dashboard's active household.
      const householdId = homes[0].id;
      const lastWateredDate = lastWatered ? new Date(lastWatered) : undefined;
      await savePlant(analysis, nickname || null, imageFile, householdId, lastWateredDate);
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Failed to save plant.");
    } finally {
      setSaving(false);
    }
  }

  function handleRetake() {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImageFile(null);
    setImagePreviewUrl(null);
    setAnalysis(null);
    setNickname("");
    setLastWatered("");
    setManualNameMode(false);
    setManualName("");
  }

  // ---------- Hidden file inputs (shared) ----------
  const fileInputs = (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </>
  );

  // ---------- MANUAL NAME VIEW ----------
  if (imagePreviewUrl && manualNameMode && !analyzing) {
    const canSubmit = manualName.trim().length > 0;
    return (
      <div className="result-root">
        {fileInputs}
        <div className="result-hero">
          <img src={imagePreviewUrl} alt="" />
          <div className="result-hero-overlay">
            <button
              type="button"
              className="icon-btn translucent"
              onClick={handleRetake}
            >
              <BackIcon />
            </button>
          </div>
        </div>
        <div className="result-card-wrap">
          <div className="card manual-card">
            <div className="manual-header">
              <div className="manual-icon">
                <AlertIcon size={18} color="#92400e" />
              </div>
              <div>
                <div className="manual-title">Couldn't identify this plant</div>
                <div className="manual-sub">
                  The photo may show damage, be unclear, or the species isn't
                  obvious. Tell us what it is and we'll still check its health.
                </div>
              </div>
            </div>
            <label className="section-label" style={{ marginTop: 4 }}>
              PLANT NAME
            </label>
            <input
              className="text-input"
              placeholder="e.g. Monstera deliciosa"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmitManualName();
              }}
            />
            <button
              type="button"
              className="primary-btn"
              style={{ marginTop: 12 }}
              disabled={!canSubmit}
              onClick={handleSubmitManualName}
            >
              Continue with this name
            </button>
          </div>
          <button
            type="button"
            className="link-btn"
            onClick={handleRetake}
            style={{ marginTop: 16 }}
          >
            ↺ Try a new photo
          </button>
        </div>
        {resultStyles}
      </div>
    );
  }

  // ---------- RESULTS VIEW ----------
  if (imagePreviewUrl && analysis && !manualNameMode) {
    const tips = analysis.careSuggestions || [];
    const diseases = analysis.diseases || [];
    const health = healthPalette(analysis.healthStatus);

    return (
      <div className="result-root">
        {fileInputs}
        <div className="result-hero">
          <img src={imagePreviewUrl} alt="" />
          <div className="result-hero-overlay">
            <button
              type="button"
              className="icon-btn translucent"
              onClick={handleRetake}
            >
              <BackIcon />
            </button>
          </div>
        </div>

        <div className="result-card-wrap">
          <div className="card result-main">
            <div className="result-main__header">
              <div style={{ flex: 1, paddingRight: 12 }}>
                <div className="result-main__name">{analysis.commonName}</div>
                <div className="result-main__sci">
                  {analysis.scientificName}
                </div>
              </div>
              <div
                className="result-main__health"
                style={{ background: health.bg, color: health.fg }}
              >
                {(analysis.healthStatus || "unknown").toUpperCase()}
              </div>
            </div>
            <div className="result-main__chips">
              {[analysis.plantType, analysis.potSize, analysis.plantSize]
                .filter(Boolean)
                .map((label, i) => (
                  <span key={i} className="chip">
                    {label}
                  </span>
                ))}
            </div>
            <div className="result-main__stats">
              <div className="stat-col">
                <div className="stat-icon">
                  <DropletIcon />
                </div>
                <div className="stat-label">WATERING</div>
                <div className="stat-value">
                  Every {analysis.wateringSchedule.frequencyDays}{" "}
                  {analysis.wateringSchedule.frequencyDays === 1 ? "day" : "days"}
                </div>
              </div>
              <div className="stat-divider" />
              <div className="stat-col">
                <div className="stat-icon">
                  <SunIcon />
                </div>
                <div className="stat-label">LIGHT</div>
                <div className="stat-value">
                  {analysis.lightNeeds || "—"}
                </div>
              </div>
            </div>
            {analysis.wateringSchedule.notes && (
              <div className="result-main__notes">
                <strong>Notes: </strong>
                {analysis.wateringSchedule.notes}
              </div>
            )}
          </div>

          {tips.length > 0 && (
            <>
              <div className="section-label">CARE TIPS</div>
              <div className="card tips-card">
                {tips.map((tip, i) => (
                  <div
                    key={i}
                    className={`tip-row ${
                      i < tips.length - 1 ? "tip-row--divider" : ""
                    }`}
                  >
                    <div className="tip-icon">
                      <LeafIcon size={16} color="#2D7A4F" />
                    </div>
                    <div className="tip-text">{tip}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {diseases.length > 0 && (
            <>
              <div className="section-label">ISSUES DETECTED</div>
              <div className="card tips-card">
                {diseases.map((d, i) => (
                  <div
                    key={i}
                    className={`tip-row ${
                      i < diseases.length - 1 ? "tip-row--divider" : ""
                    }`}
                  >
                    <div className="tip-icon issue-icon">
                      <AlertIcon />
                    </div>
                    <div className="tip-text">{d}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="section-label">LAST WATERED</div>
          <div className="card last-watered-card">
            <div className="last-watered-options">
              {[
                { label: "Today", value: 0 },
                { label: "Yesterday", value: 1 },
                { label: "3 days ago", value: 3 },
                { label: "A week ago", value: 7 },
              ].map((opt) => {
                const d = new Date();
                d.setDate(d.getDate() - opt.value);
                const val = d.toISOString().split("T")[0];
                const selected = lastWatered === val;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`last-watered-chip ${selected ? "last-watered-chip--active" : ""}`}
                    onClick={() => setLastWatered(val)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="last-watered-custom">
              <span className="last-watered-custom-label">Or pick a date:</span>
              <input
                type="date"
                className="text-input"
                value={lastWatered}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setLastWatered(e.target.value)}
                style={{ border: "none", padding: "6px 0", flex: 1 }}
              />
            </div>
          </div>

          <div className="section-label">NICKNAME</div>
          <div className="card">
            <input
              className="text-input"
              placeholder="Give it a nickname (optional)"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              style={{ border: "none", padding: "6px 0" }}
            />
          </div>

          <button
            type="button"
            className="link-btn"
            onClick={handleRetake}
          >
            ↺ Retake photo
          </button>
        </div>

        <div className="sticky-cta-wrap">
          <button
            type="button"
            className="primary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Plant"}
          </button>
        </div>
        {resultStyles}
      </div>
    );
  }

  // ---------- HERO / ANALYZING VIEW ----------
  return (
    <div className="app-shell add-plant">
      {fileInputs}
      <div className="header-row">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate(-1)}
        >
          <BackIcon />
        </button>
        <h1 className="header-title">Add Plant</h1>
        <div style={{ width: 40, height: 40 }} />
      </div>

      {!imagePreviewUrl ? (
        <div className="add-plant__hero">
          <div className="add-plant__icon-wrap">
            <div className="add-plant__icon-card">
              <SparkleIcon />
            </div>
            <div className="add-plant__camera-badge">
              <CameraIcon size={14} color="#fff" />
            </div>
          </div>
          <h2 className="add-plant__title">Identify with photo</h2>
          <p className="add-plant__sub">
            Take a photo or upload from gallery to identify your plant
            instantly with AI
          </p>
          <div className="add-plant__actions">
            <label className="primary-btn add-plant__file-label">
              <CameraIcon color="#fff" />
              Take Photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </label>
            <label className="secondary-btn add-plant__file-label">
              <GalleryIcon />
              Choose from Gallery
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </label>
          </div>
          <div className="add-plant__tip">
            <span role="img" aria-hidden="true">💡</span>
            <span>
              Clear, well-lit photos of healthy, undamaged foliage help the
              AI identify your plant most accurately.
            </span>
          </div>
        </div>
      ) : (
        <img
          src={imagePreviewUrl}
          className="add-plant__preview"
          alt=""
        />
      )}

      {analyzing && (
        <div className="card add-plant__loading">
          <Lottie
            animationData={plantScanningAnimation}
            loop
            autoplay
            className="add-plant__lottie"
          />
          <div className="add-plant__loading-title">Identifying your plant…</div>
          <div className="add-plant__loading-sub">
            This usually takes a few seconds.
          </div>
        </div>
      )}

      <style>{`
        .add-plant__hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 28px;
        }
        .add-plant__icon-wrap {
          position: relative;
          width: 96px;
          height: 96px;
          margin-bottom: 20px;
        }
        .add-plant__icon-card {
          width: 96px;
          height: 96px;
          border-radius: 24px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.08);
        }
        .add-plant__camera-badge {
          position: absolute;
          top: -4px; right: -4px;
          width: 28px; height: 28px;
          border-radius: 14px;
          background: #2D7A4F;
          display: flex; align-items: center; justify-content: center;
          border: 2.5px solid #F0F5F0;
        }
        .add-plant__title {
          font-family: Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #1f5a39;
          margin: 0 0 8px 0;
          text-align: center;
        }
        .add-plant__sub {
          font-size: 13px;
          color: #6b7a6e;
          text-align: center;
          max-width: 300px;
          line-height: 19px;
          margin: 0 0 28px 0;
        }
        .add-plant__file-label {
          cursor: pointer;
        }
        .add-plant__actions {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 22px;
        }
        .add-plant__tip {
          background: #E8F5EE;
          border-radius: 14px;
          padding: 12px 14px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 12px;
          color: #1f5a39;
          line-height: 18px;
          margin-top: 4px;
        }
        .add-plant__preview {
          width: 100%;
          height: 250px;
          border-radius: 16px;
          object-fit: cover;
          margin-bottom: 16px;
        }
        .add-plant__loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 20px 28px 28px;
        }
        .add-plant__lottie {
          width: 160px;
          height: 160px;
        }
        .add-plant__loading-title {
          font-size: 15px; font-weight: 700; color: #1f5a39;
        }
        .add-plant__loading-sub {
          font-size: 12px; color: #6b7a6e;
        }
      `}</style>
    </div>
  );
}

const resultStyles = (
  <style>{`
    .result-root {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #F0F5F0;
      padding-bottom: 120px;
      max-width: 480px;
      margin: 0 auto;
      width: 100%;
      position: relative;
    }
    .result-hero {
      width: 100%;
      height: 50vh;
      max-height: 420px;
      background: #cbd5cb;
      position: relative;
    }
    .result-hero img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .result-hero-overlay {
      position: absolute;
      top: env(safe-area-inset-top, 12px);
      left: 16px;
    }
    .icon-btn.translucent {
      background: rgba(255,255,255,0.85);
    }
    .result-card-wrap {
      padding: 0 16px;
      margin-top: -28px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .result-main { border-radius: 24px; padding: 20px; }
    .result-main__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 14px;
    }
    .result-main__name {
      font-family: Georgia, serif;
      font-size: 26px;
      font-weight: 700;
      color: #1f5a39;
    }
    .result-main__sci {
      font-size: 13px;
      font-style: italic;
      color: #2D7A4F;
      margin-top: 4px;
    }
    .result-main__health {
      padding: 5px 10px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.8px;
    }
    .result-main__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 12px;
    }
    .chip {
      background: #E8F5EE;
      padding: 5px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      color: #1f5a39;
      text-transform: capitalize;
    }
    .result-main__stats {
      display: flex;
      align-items: stretch;
      padding: 10px 0;
    }
    .stat-col { flex: 1; display: flex; flex-direction: column; gap: 6px; }
    .stat-divider { width: 1px; background: #d9e1da; margin: 0 12px; }
    .stat-icon {
      width: 36px;
      height: 36px;
      border-radius: 18px;
      background: #2D7A4F;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 4px;
    }
    .stat-label {
      font-size: 10px;
      font-weight: 700;
      color: #6b7a6e;
      letter-spacing: 0.6px;
    }
    .stat-value {
      font-size: 15px;
      font-weight: 700;
      color: #1a2e22;
    }
    .result-main__notes {
      margin-top: 14px;
      padding-top: 14px;
      border-top: 1px solid #d9e1da;
      font-size: 13px;
      color: #1a2e22;
      line-height: 19px;
    }
    .result-main__notes strong { color: #6b7a6e; font-weight: 700; }
    .tips-card {
      padding: 0;
    }
    .tip-row {
      display: flex;
      align-items: flex-start;
      padding: 14px 16px;
      gap: 12px;
    }
    .tip-row--divider {
      border-bottom: 1px solid #e6ede7;
    }
    .tip-icon {
      width: 32px;
      height: 32px;
      border-radius: 16px;
      background: #e6f0e8;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 1px;
      flex-shrink: 0;
    }
    .tip-icon.issue-icon { background: #fee2e2; }
    .tip-text {
      flex: 1;
      font-size: 14px;
      color: #1a2e22;
      line-height: 20px;
    }
    .manual-card { padding: 20px; }
    .manual-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;
    }
    .manual-icon {
      width: 36px;
      height: 36px;
      border-radius: 18px;
      background: #fef3c7;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .manual-title {
      font-family: Georgia, serif;
      font-size: 17px;
      font-weight: 700;
      color: #1f5a39;
      margin-bottom: 4px;
    }
    .manual-sub {
      font-size: 13px;
      color: #6b7a6e;
      line-height: 18px;
    }
    .last-watered-card { padding: 14px 16px; }
    .last-watered-options {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
    }
    .last-watered-chip {
      padding: 7px 14px;
      border-radius: 999px;
      border: 1.5px solid #dbe8df;
      background: #fff;
      font-size: 13px;
      font-weight: 600;
      color: #1a2e22;
      cursor: pointer;
    }
    .last-watered-chip--active {
      background: #2D7A4F;
      border-color: #2D7A4F;
      color: #fff;
    }
    .last-watered-custom {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-top: 8px;
      border-top: 1px solid #e6ede7;
    }
    .last-watered-custom-label {
      font-size: 12px;
      color: #6b7a6e;
      white-space: nowrap;
    }
    .link-btn {
      background: none;
      border: none;
      color: #6b7a6e;
      font-size: 13px;
      font-weight: 600;
      padding: 14px;
      align-self: center;
    }
  `}</style>
);
